import { ApiError } from '../api/client';
import { t } from '../i18n';

const API_ERROR_KEYS: Record<string, string> = {
  vault_version_regression: 'errors.vaultVersionRegression',
  vault_not_setup: 'errors.vaultNotSetup',
  manifest_revision_mismatch: 'errors.manifestRevisionMismatch',
  guest_upload_file_limit: 'errors.guestUploadFileLimit',
  guest_upload_byte_limit: 'errors.guestUploadByteLimit',
  verify_locked_out: 'errors.verifyLockedOut',
  rate_limited: 'errors.rateLimited',
  blob_not_owned: 'errors.blobNotOwned',
  blob_already_linked: 'errors.blobAlreadyLinked',
  blob_missing: 'errors.blobMissing',
  blob_not_found: 'errors.blobNotFound',
  file_not_found: 'errors.fileNotFound',
  reply_budget_exhausted: 'errors.replyBudgetExhausted',
  csrf_invalid: 'errors.csrfInvalid',
  expires_at_too_far: 'errors.expiresAtTooFar',
  expires_at_must_be_future: 'errors.expiresAtMustBeFuture',
  request_too_large: 'errors.requestTooLarge',
  request_timeout: 'errors.requestTimeout',
  sms_send_failed: 'info.owner.smsSendFailed',
  sms_not_configured: 'info.owner.smsSendFailed',
  sms_provider_error: 'info.owner.smsSendFailed',
  invalid_phone_number: 'info.owner.smsPhoneInvalid',
};

/** Snake_case machine codes from client throws (e.g. empty_file_name). */
const SAFE_ERROR_CODE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+){1,8}$/;

function isAbortError(cause: unknown): boolean {
  if (!(cause instanceof Error)) {
    return false;
  }

  if (cause.name === 'AbortError' || cause.name === 'TimeoutError') {
    return true;
  }

  return cause.message === 'The operation was aborted.'
    || cause.message === 'The user aborted a request.';
}

/** User-facing message for an API/client failure. Prefer mapped i18n; else show the API code. */
export function apiErrorMessage(cause: unknown, fallbackKey = 'status.error'): string {
  if (isAbortError(cause)) {
    return t('errors.requestTimeout');
  }

  if (cause instanceof ApiError) {
    const key = API_ERROR_KEYS[cause.code];
    if (key) {
      return t(key);
    }

    if (cause.code && cause.code !== 'request_failed') {
      return `${t(fallbackKey)} (${cause.code})`;
    }
  }

  if (cause instanceof Error && cause.message && cause.message !== 'status.error') {
    const key = API_ERROR_KEYS[cause.message];
    if (key) {
      return t(key);
    }

    if (SAFE_ERROR_CODE.test(cause.message)) {
      return `${t(fallbackKey)} (${cause.message})`;
    }
  }

  return t(fallbackKey);
}
